import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { ChevronDown, ChevronUp, School as SchoolIcon, Layers, GraduationCap, Users, Info } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

/**
 * Compact, dismissible explainer that shows the academic hierarchy:
 *   School → Stage → Grade → Class → Students
 *
 * `highlight` accepts one of: "stage" | "grade" | "class" — the matching
 * node is rendered with an emphasis ring so the reader can tell where in
 * the hierarchy the current page sits.
 */
export default function AcademicHierarchyHelper({ highlight }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(true);

  const nodes = [
    { key: "school", icon: SchoolIcon, label: t("hint_school"), desc: t("hint_school_desc"), color: "slate" },
    { key: "stage", icon: Layers, label: t("hint_stage"), desc: t("hint_stage_desc"), color: "violet" },
    { key: "grade", icon: GraduationCap, label: t("hint_grade"), desc: t("hint_grade_desc"), color: "emerald" },
    { key: "class", icon: Users, label: t("hint_class"), desc: t("hint_class_desc"), color: "blue" },
  ];

  const ringFor = (key) => {
    if (highlight !== key) return "";
    return {
      stage: "ring-2 ring-violet-400 dark:ring-violet-500",
      grade: "ring-2 ring-emerald-400 dark:ring-emerald-500",
      class: "ring-2 ring-blue-400 dark:ring-blue-500",
    }[key] || "";
  };

  const bgFor = (color) => ({
    slate:   "bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700",
    violet:  "bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-200 border-violet-200 dark:border-violet-800",
    emerald: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
    blue:    "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-800",
  }[color]);

  return (
    <Card className="border shadow-none overflow-hidden" data-testid="hierarchy-helper">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-start hover:bg-muted/40 transition"
        onClick={() => setOpen(!open)}
        data-testid="hierarchy-helper-toggle"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">{t("hint_title")}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border">
          {/* Visual hierarchy chain */}
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="hierarchy-chain">
            {nodes.map((n, i) => {
              const Icon = n.icon;
              return (
                <div key={n.key} className="flex items-center gap-2">
                  <div
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${bgFor(n.color)} ${ringFor(n.key)}`}
                    data-testid={`hierarchy-node-${n.key}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold">{n.label}</span>
                  </div>
                  {i < nodes.length - 1 && (
                    <span className="text-muted-foreground select-none rtl:rotate-180">→</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Per-node short description */}
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {nodes.slice(1).map((n) => (
              <div
                key={n.key}
                className={`text-xs rounded-lg px-3 py-2 border ${bgFor(n.color)} ${
                  highlight === n.key ? "font-semibold" : "opacity-90"
                }`}
                data-testid={`hierarchy-desc-${n.key}`}
              >
                <strong>{n.label}:</strong> {n.desc}
              </div>
            ))}
          </div>

          <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2 border border-dashed">
            <strong>{t("hint_example_title")}:</strong> {t("hint_example_body")}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => setOpen(false)}
            data-testid="hierarchy-helper-close"
          >
            {t("hint_got_it")}
          </Button>
        </div>
      )}
    </Card>
  );
}
